import {Injectable} from '@angular/core';
import {firstValueFrom, map} from "rxjs";
import {environment} from "../../../../../environments/environment";
import {
    AdherenceCharts,
    Caseload,
    TimelineData,
    WeeklySummary
} from "@shared/api/models/query/patient-statistics.model";
import {DailySummaryV2Response} from "@shared/api/models/query/patient-daily-snapshot.model";
import {
    CarerWeeklySnapshotV2,
    PatientWeeklySnapshotV2,
    WeeklyNoSnapshot
} from "@shared/api/models/query/patient-weekly-snapshot.model";
import {PaginatorService} from "@shared/api/services/paginator.service";
import {PatientBasic} from "@shared/api/models/query/patient.model";

@Injectable({
    providedIn: 'root'
})
export class PatientStatisticsService extends PaginatorService<PatientBasic> {

    protected override get domainEntity() {
        return 'patients';
    }

    async getWeeklySummary(patientId: number, dateStart: string, dateEnd: string, type: string): Promise<WeeklySummary> {
        const params = {dateStart, dateEnd, type};
        const result = this.http.get<{data: WeeklySummary}>(
            this.urlToEntity + '/' + patientId + '/statistics/weekly',
            {params}
        ).pipe(map((response) => response?.data ?? null));
        return firstValueFrom(result);
    }

    getPatientTimeline(
        patientId: number,
        dateStart: string,
        dateEnd: string,
    ): Promise<TimelineData> {
        return this.getTimeline(patientId, dateStart, dateEnd, 'patient');
    }

    getTimeline(
        patientId: number,
        dateStart: string,
        dateEnd: string,
        type: 'patient'
    ): Promise<TimelineData>;

    getTimeline(
        patientId: number,
        dateStart: string,
        dateEnd: string,
        type: 'carer'
    ): Promise<TimelineData>;

    async getTimeline(patientId: number, dateStart: string, dateEnd: string, type: string): Promise<TimelineData> {
        const params = {dateStart, dateEnd, type};
        const result = this.http.get<{data: TimelineData}>(
            this.urlToEntity + '/' + patientId + '/statistics/timeline',
            {params}
        ).pipe(map((response) => response?.data ?? null));
        return firstValueFrom(result);
    }

    async getCharts(patientId: number, type: string): Promise<AdherenceCharts> {
        const params = {type};
        const result = this.http.get<{data: AdherenceCharts}>(
            this.urlToEntity + '/' + patientId + '/statistics/charts',
            {params}
        ).pipe(map((response) => response?.data ?? null));
        return firstValueFrom(result);
    }

    async getCaseload(): Promise<Caseload> {
        const result = this.http.get<{data: Caseload}>(environment.baseApiUrl + 'v2/statistics/patient-caseload')
            .pipe(map((response) => response?.data ?? null));
        return firstValueFrom(result);
    }

    getDailySnapshot(
        patientId: number,
        date: string,
        type: 'patient'
    ): Promise<DailySummaryV2Response>;

    getDailySnapshot(
        patientId: number,
        date: string,
        type: 'carer'
    ): Promise<DailySummaryV2Response>;

    async getDailySnapshot(patientId: number, date: string, type: string): Promise<DailySummaryV2Response> {
        const params = {date, type};
        const result = this.http.get<{data: DailySummaryV2Response}>(
            this.urlToEntity + '/' + patientId + '/statistics/daily',
            {params}
        ).pipe(map((response) => response?.data ?? null));
        return firstValueFrom(result);
    }

    // v2 contract (UNIVAPLAT-3033): unwraps the {data} envelope the same way
    // getDailySnapshot does. Narrows to the v2 or no-snapshot variant via
    // isWeeklySnapshotV2 from the weekly snapshot model. A pre-regeneration row (no
    // payloadVersion) is no longer separately typed — UNIVAPLAT-3042 treats it
    // identically to WeeklyNoSnapshot at every consumer.
    getWeeklyDetail(
        patientId: number,
        dateStart: string,
        type: 'patient'
    ): Promise<PatientWeeklySnapshotV2 | WeeklyNoSnapshot>;

    getWeeklyDetail(
        patientId: number,
        dateStart: string,
        type: 'carer'
    ): Promise<CarerWeeklySnapshotV2 | WeeklyNoSnapshot>;

    async getWeeklyDetail(
        patientId: number,
        dateStart: string,
        type: string
    ): Promise<PatientWeeklySnapshotV2 | CarerWeeklySnapshotV2 | WeeklyNoSnapshot> {
        const params = {dateStart, type};
        const result = this.http.get<{
            data: PatientWeeklySnapshotV2 | CarerWeeklySnapshotV2 | WeeklyNoSnapshot
        }>(
            this.urlToEntity + '/' + patientId + '/statistics/weekly/detail',
            {params}
        ).pipe(map((response) => response?.data ?? null));
        return firstValueFrom(result);
    }
}
